import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// Crea el contexto de autenticación
export const AuthContext = createContext();

// Define el tiempo de inactividad de la sesión en milisegundos (ej. 5 minutos)
const SESSION_TIMEOUT_MS = 5 * 60 * 1000;
// URL base de la API - ProyectoRP backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://riasbaixaspd.es/api';

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [userId, setUserId] = useState(null);

  const navigate = useNavigate();

  // Cargar autenticación desde localStorage al iniciar
  useEffect(() => {
    try {
      const storedAuth = localStorage.getItem('auth');
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        setIsLoggedIn(!!parsed.isLoggedIn);
        setUser(parsed.user || null);
        setPermissions(Array.isArray(parsed.permissions) ? parsed.permissions : []);
        setAuthToken(parsed.authToken || null);
        setUserId(parsed.userId || null);
      }
    } catch (e) {
      console.error('Fallo al parsear la autenticación de localStorage:', e);
      localStorage.removeItem('auth');
    } finally {
      setLoadingAuth(false);
    }
  }, []);

  // Guardar autenticación en localStorage cuando cambie
  useEffect(() => {
    if (!loadingAuth) {
      localStorage.setItem(
        'auth',
        JSON.stringify({ isLoggedIn, user, permissions, authToken, userId })
      );
    }
  }, [isLoggedIn, user, permissions, loadingAuth, authToken, userId]);

  // Función para manejar el inicio de sesión
  const handleLogin = useCallback(async (username, password) => {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] API login endpoint:', `${API_BASE_URL}/login`);
      }
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || 'Credenciales inválidas. Por favor, inténtalo de nuevo.');
        } catch (jsonError) {
          throw new Error(errorText || 'Credenciales inválidas. Por favor, inténtalo de nuevo.');
        }
      }

      const data = await response.json();

      const token = data.token;
      const role = data.role;
      const id = data.id;
      const permissions = data.permissions;

      setIsLoggedIn(true);
      setUser({ id: id, username: username, role: role, permissions: permissions });
      setPermissions(permissions);
      setAuthToken(token);
      setUserId(id);

      localStorage.setItem('authToken', token);
      localStorage.setItem('userRole', role);
      localStorage.setItem('userId', id);
      localStorage.setItem('auth', JSON.stringify({ isLoggedIn: true, user: { id: id, username: username, role: role, permissions: permissions }, permissions: permissions, authToken: token, userId: id }));

      if (role === 'superadmin' || permissions.includes('ver_dashboard')) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
      return true;
    } catch (error) {
      console.error('Error durante el inicio de sesión:', error);
      setIsLoggedIn(false);
      setUser(null);
      setPermissions([]);
      setAuthToken(null);
      setUserId(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('auth');
      throw error;
    }
  }, [navigate]);

  // Función para manejar inicio de sesión con Discord
  const handleDiscordLogin = useCallback(async (code) => {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] API Discord login callback:', `${API_BASE_URL}/auth/discord/callback`);
      }
      const response = await fetch(`${API_BASE_URL}/auth/discord/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || 'Error de inicio de sesión con Discord. Inténtalo de nuevo.');
        } catch (jsonError) {
          throw new Error(errorText || 'Error de inicio de sesión con Discord. Inténtalo de nuevo.');
        }
      }

      const data = await response.json();

      const token = data.token;
      const id = data.user.id;
      const username = data.user.username;
      const role = data.user.role;
      const permissions = data.user.permissions || ['ver_propia_whitelist'];
      const hasMemberRole = data.user.hasMemberRole;

      setIsLoggedIn(true);
      setUser({ id, username, role, permissions, discord_id: id, avatar: data.user.avatar, hasMemberRole });
      setPermissions(permissions);
      setAuthToken(token);
      setUserId(id);

      localStorage.setItem('authToken', token);
      localStorage.setItem('userRole', role);
      localStorage.setItem('userId', id);
      localStorage.setItem('auth', JSON.stringify({
        isLoggedIn: true,
        user: { id, username, role, permissions, discord_id: id, avatar: data.user.avatar, hasMemberRole },
        permissions,
        authToken: token,
        userId: id
      }));

      // Siempre redirigir al dashboard del usuario (si es staff verá opción en su perfil)
      navigate('/dashboard');
      return true;
    } catch (error) {
      console.error('Error durante el inicio de sesión de Discord:', error);
      setIsLoggedIn(false);
      setUser(null);
      setPermissions([]);
      setAuthToken(null);
      setUserId(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('auth');
      throw error;
    }
  }, [navigate]);

  // Logout
  const logout = useCallback(() => {
    setIsLoggedIn(false);
    setUser(null);
    setPermissions([]);
    setAuthToken(null);
    setUserId(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('auth');
    navigate('/login');
  }, [navigate]);

  // Función para validar token
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem('authToken') || authToken;
    if (!token) {
      console.log('❌ No hay token disponible');
      logout();
      return false;
    }

    try {
      console.log('🔍 Validando token...');
      const response = await fetch(`${API_BASE_URL}/auth/validate`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('✅ Token válido');
        const data = await response.json();
        if (data.ok && data.user) {
          setUser(prev => {
            const updated = { ...prev, ...data.user };
            localStorage.setItem('auth', JSON.stringify({
              isLoggedIn: true,
              user: updated,
              permissions: updated.permissions,
              authToken: token,
              userId: updated.id
            }));
            return updated;
          });
        }
        return true;
      } else if (response.status === 401) {
        console.log('❌ Token expirado o inválido');
        logout();
        return false;
      } else {
        console.log('⚠️ Error al validar token:', response.status);
        return false;
      }
    } catch (error) {
      console.error('❌ Error validando token:', error);
      return false;
    }
  }, [authToken, logout]);

  // Comprueba si el usuario tiene un permiso concreto
  const hasPermission = useCallback(
    (permission) =>
      Array.isArray(permissions) && permissions.includes(permission),
    [permissions]
  );

  if (loadingAuth) {
    return <div>Cargando autenticación...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        permissions,
        handleLogin,
        handleDiscordLogin,
        logout,
        hasPermission,
        validateToken,
        loadingAuth,
        userId: user?.id || localStorage.getItem('userId') || null,
        authToken: authToken || localStorage.getItem('authToken') || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar el contexto fácilmente
export const useAuth = () => useContext(AuthContext);